import NewsCoverageAnalysis from './NewsCoverageAnalysis';
import NewsCoverageSection from './NewsCoverageSection';
import type { NewsAnalysisResponse } from './types';

interface NewsAnalysisSectionProps {
  news: NewsAnalysisResponse | null | undefined;
  /** Instagram shows a single "News Coverage" card (banner + carousel);
   *  YouTube splits it into "News coverage" (analysis) + "News Articles". */
  isInstagram?: boolean;
}

export default function NewsAnalysisSection({ news, isInstagram = false }: NewsAnalysisSectionProps) {
  if (!news) return null;

  const articles = Array.isArray(news.articles) ? news.articles : [];
  const total = typeof news.total_articles === 'number' ? news.total_articles : articles.length;
  const analysis = news.analysis ?? null;
  const error = typeof news.error === 'string' ? news.error : undefined;

  if (total === 0 && articles.length === 0 && !error) return null;

  // Two sections (Figma 2165-108189): "News coverage" (analysis) above,
  // "News Articles" (banner + slider) below.
  if (error) {
    return (
      <div className="mb-4 rounded-2xl border border-border bg-card p-6">
        <div className="text-heading-h6 text-foreground">News coverage</div>
        <div className="mt-3 rounded-xl border border-dashed border-red-200 bg-red-50 p-3 text-body-sm-regular leading-[1.55] text-red-700">
          {error}
        </div>
      </div>
    );
  }

  // Instagram: one "News Coverage" card (banner + carousel).
  if (isInstagram) {
    return <NewsCoverageSection title="News Coverage" articles={articles} total={total} analysis={analysis} />;
  }

  // YouTube: "News coverage" (analysis) + "News Articles" (banner + carousel).
  return (
    <>
      {analysis && <NewsCoverageAnalysis analysis={analysis} />}
      <NewsCoverageSection title="News Articles" articles={articles} total={total} analysis={analysis} />
    </>
  );
}
